import {
	Alert,
	Box,
	Button,
	DateTimeInput,
	Divider,
	Heading,
	Icons,
	Inline,
	Money,
	Select,
	Spinner,
	Stack,
	Text,
	Tooltip,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import {
	arrayMutators,
	DatePickerField,
	FieldArray,
	FieldArrayErrorMessage,
	Form,
	GetFieldValue,
	SubmissionError,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { Link } from "@sembark-travel/ui-router"
import { themeContractVars } from "@sembark-travel/ui-theme"
import {
	addUnit,
	dateToQuery,
	formatDate,
	getUnit,
	isSame,
	parseDateFromQuery,
	startOf,
	subtractUnit,
} from "@sembark-travel/datetime-utils"
import { moneyParseByDecimal, type TMoney } from "@sembark-travel/money"
import { useXHR } from "@sembark-travel/xhr"
import pluralize from "pluralize"
import { Fragment, useEffect, useMemo, useRef } from "react"
import useSWR from "swr"
import * as Validator from "yup"
import { PERMISSIONS, useCheckPermissions, useHasFeatureFlag } from "../Auth"
import { ChannelManagerSyncAriDialog } from "../ChannelManagers"
import {
	OtaMealCategoryBadge,
	type THotelOtaMealPlan,
} from "../HotelOtaMealPlans"
import type { THotelOtaRoomType } from "../HotelOtaRoomTypes"
import {
	IntervalDaysSelector,
	newInterval,
	testNoOverlappingIntervals,
	type TSeasonFormInterval,
} from "../RateSheets"
import { generatePath, useSearchParams } from "../router-utils"
import { SelectTripSources } from "../TripSources"
import type { ITripSource } from "../TripSources"
import {
	hotelsXHR,
	type IHotel,
	type THotelCalendarAriDay,
	type THotelCalendarAriRoomType,
} from "./store"
import { getCalendarDays } from "./utils"

type TCalendarCellStatus = "open" | "sold_out" | "stop_sell"

// A room type with this many rooms left or fewer is flagged amber
const LOW_ROOMS_LEFT = 2

type TTripSourceFilter = Pick<ITripSource, "id" | "name">

// The trip source travels in the url as `${id}_${name}` so the select can show
// its label on reload without another request (see HotelBookings filters).
function tripSourceFromQuery(value: string | null): TTripSourceFilter | null {
	if (!value) return null
	const [id, ...name] = value.split("_")
	if (!Number(id)) return null
	return { id: Number(id), name: name.join("_") }
}

function tripSourceToQuery(tripSource: TTripSourceFilter) {
	return `${tripSource.id}_${tripSource.name}`
}

export function HotelCalendar({ hotel }: { hotel: IHotel }) {
	const xhr = useXHR()
	const [searchParams, setSearchParams] = useSearchParams()
	// syncing writes real rates and inventory through the channel manager, so
	// keep it to the tenants that have the simulator enabled
	const { hasPermission } = useCheckPermissions()
	const canSyncAri =
		useHasFeatureFlag("cm_simulator") && hasPermission(PERMISSIONS.MANAGE_USERS)

	const today = startOf(new Date(), "day")
	const fromQuery = searchParams.get("from")
	const from = fromQuery
		? parseDateFromQuery(fromQuery, undefined, today)
		: today
	const days = getCalendarDays(from)

	const otaMealPlan =
		hotel.ota_meal_plans.find(
			(mealPlan) => String(mealPlan.id) === searchParams.get("ota_meal_plan")
		) ||
		hotel.ota_meal_plans.find((mealPlan) => mealPlan.meal_category === "CP") ||
		hotel.ota_meal_plans[0]

	// without a DMC the api returns the calendar rates
	const tripSource = tripSourceFromQuery(searchParams.get("trip_source"))

	const startDate = dateToQuery(days[0])
	const endDate = dateToQuery(days[days.length - 1])
	const { data: calendarAri, error } = useSWR(
		otaMealPlan
			? `/hotels/${hotel.id}/calendar-ari?${startDate}_${endDate}_${otaMealPlan.id}_${tripSource?.id ?? "calendar"}`
			: null,
		() =>
			hotelsXHR(xhr).getCalendarAri(hotel.id, {
				start_date: startDate,
				end_date: endDate,
				ota_meal_plan_id: otaMealPlan.id,
				trip_source_id: tripSource?.id,
			})
	)

	function updateSearchParams(changes: Record<string, string | null>) {
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev)
				Object.entries(changes).forEach(([key, value]) => {
					if (value === null) {
						next.delete(key)
					} else {
						next.set(key, value)
					}
				})
				return next
			},
			{ replace: true }
		)
	}

	return (
		<Stack gap="6" padding="6">
			<Inline justifyContent="between" alignItems="center">
				<Heading as="h4">Calendar</Heading>
				{hotel.channel_manager && canSyncAri ? (
					<ChannelManagerSyncAriDialog hotel={hotel}>
						{({ open }) => (
							<Button level="primary" onClick={open}>
								Sync ARI
							</Button>
						)}
					</ChannelManagerSyncAriDialog>
				) : null}
				{/* Pending API: the "set rates" endpoint doesn't exist yet, so the
				    Set Rates button (shown for hotels without a channel manager) is
				    hidden. Enable it once BE ships the API.
				{!hotel.channel_manager && hasPermission(PERMISSIONS.MANAGE_USERS) ? (
					<IntervalsInDialog
						title="Add Interval"
						onSuccess={(intervals) => {
							navigate("/hotels/:hotelId/add-rates", {
								params: { hotelId: String(hotel.id) },
								state: { intervals },
							})
						}}
					>
						{({ open }) => (
							<Button level="primary" onClick={open}>
								Set Rates
							</Button>
						)}
					</IntervalsInDialog>
				) : null} */}
			</Inline>
			<Inline gap="4" justifyContent="between" alignItems="end" flexWrap="wrap">
				<Inline gap="4" alignItems="end" flexWrap="wrap">
					<SelectTripSources
						label="B2B Source"
						placeholder="Calendar rates"
						multiple={false}
						isAgent={1}
						fetchOnMount
						minWidth="16rem"
						value={tripSource}
						onChange={(selected: ITripSource | null) =>
							updateSearchParams({
								trip_source: selected ? tripSourceToQuery(selected) : null,
							})
						}
					/>
					<Select
						label="Meal Plan"
						options={hotel.ota_meal_plans}
						value={otaMealPlan}
						labelKey="name"
						clearable={false}
						minWidth="16rem"
						optionRenderer={OtaMealPlanOption}
						onChange={(selected: THotelOtaMealPlan) =>
							updateSearchParams({ ota_meal_plan: String(selected.id) })
						}
					/>
				</Inline>
				<WeekNavigator
					days={days}
					onChange={(nextFrom) =>
						updateSearchParams({ from: dateToQuery(nextFrom) })
					}
				/>
			</Inline>
			{otaMealPlan ? (
				error ? (
					<Alert status="error">{error.message}</Alert>
				) : !calendarAri ? (
					<Spinner padding="4" alignCenter />
				) : (
					<CalendarTable
						roomTypes={calendarAri.ota_room_types}
						days={days}
						today={today}
						otaMealPlan={otaMealPlan}
					/>
				)
			) : (
				<Alert status="warning">
					No OTA meal plans are configured for this hotel.{" "}
					<Link
						to={generatePath("/hotels/:hotelId/meal-plans", {
							hotelId: String(hotel.id),
						})}
					>
						Add a meal plan
					</Link>
				</Alert>
			)}
		</Stack>
	)
}

const intervalsValidationSchema = Validator.object({
	intervals: Validator.array()
		.of(
			Validator.object({
				from: Validator.date()
					.nullable()
					.required("Required")
					.test("no-overlapping-intervals", "", function () {
						return testNoOverlappingIntervals(this)
					}),
				to: Validator.date()
					.nullable()
					.required("Required")
					.min(Validator.ref("from"), "Must be on or after the From date"),
				days: Validator.array().min(1, "Please select at least one day."),
			})
		)
		.min(1, "Add at least one interval"),
})

interface IIntervalsFormSchema {
	intervals: TSeasonFormInterval[]
}

function IntervalsForm({
	initialValues,
	submitLabel,
	onSubmit,
}: {
	initialValues?: IIntervalsFormSchema
	submitLabel: string
	onSubmit: (values: IIntervalsFormSchema) => void | Promise<void>
}) {
	const formInitialValues = useMemo(
		() => initialValues || { intervals: [newInterval()] },
		[initialValues]
	)
	return (
		<Form<IIntervalsFormSchema>
			mutators={{ ...arrayMutators }}
			initialValues={formInitialValues}
			validate={validateFormValues(intervalsValidationSchema)}
			onSubmit={withServerErrors(async (values) => {
				await onSubmit(values)
			})}
			subscription={{ submitting: true }}
		>
			{({ handleSubmit, submitting }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<FieldArray name="intervals">
							{({ fields }) => (
								<>
									{fields.map((name, iIdx) => (
										<Fragment key={name}>
											{iIdx > 0 ? <Divider marginY="0" /> : null}
											<Stack gap="2">
												<Inline gap="4" alignItems="end" flexWrap="wrap">
													<Inline gap="4">
														<DatePickerField
															label="From"
															name={`${name}.from`}
															dateFormat="DD MMM, YYYY"
														/>
														<GetFieldValue<TSeasonFormInterval["from"]>
															name={`${name}.from`}
														>
															{({ value: from }) => (
																<DatePickerField
																	label="To"
																	name={`${name}.to`}
																	min={from || undefined}
																	dateFormat="DD MMM, YYYY"
																/>
															)}
														</GetFieldValue>
													</Inline>
													{(fields.length || 0) > 1 ? (
														<Button
															level="tertiary"
															size="sm"
															type="button"
															aria-label="Remove interval"
															onClick={() => fields.remove(iIdx)}
														>
															<Icons.Cancel />
														</Button>
													) : null}
												</Inline>
												<IntervalDaysSelector name={`${name}.days`} />
											</Stack>
										</Fragment>
									))}
									<Box paddingTop="2">
										<Button
											level="tertiary"
											size="sm"
											type="button"
											color="primary"
											aria-label="Add another interval"
											onClick={() => fields.push(newInterval())}
										>
											<Icons.Plus />
											Add Interval
										</Button>
									</Box>
								</>
							)}
						</FieldArray>
						<FieldArrayErrorMessage name="intervals" />
						<SubmissionError />
						<Inline justifyContent="end">
							<Button level="primary" type="submit" disabled={submitting}>
								{submitting ? <Icons.Spinner /> : null}
								{submitLabel}
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

export function IntervalsInDialog({
	title,
	initialIntervals,
	onSuccess,
	children,
}: {
	title: string
	initialIntervals?: TSeasonFormInterval[]
	onSuccess: (intervals: TSeasonFormInterval[]) => void
	children: (props: { open: () => void }) => React.ReactNode
}) {
	const [isOpen, open, close] = useDialog()
	const initialValues = useMemo(
		() => (initialIntervals ? { intervals: initialIntervals } : undefined),
		[initialIntervals]
	)

	return (
		<>
			{children({ open })}
			<Dialog open={isOpen} onClose={close} title={title} sm>
				<Dialog.Body>
					<IntervalsForm
						initialValues={initialValues}
						submitLabel={title}
						onSubmit={({ intervals }) => {
							onSuccess(intervals)
							close()
						}}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function OtaMealPlanOption({ option }: { option: THotelOtaMealPlan }) {
	return (
		<Inline gap="2" alignItems="center">
			<Box as="span">{option.name}</Box>
			<OtaMealCategoryBadge category={option.meal_category} />
		</Inline>
	)
}

function WeekNavigator({
	days,
	onChange,
}: {
	days: Date[]
	onChange: (from: Date) => void
}) {
	const first = days[0]
	const last = days[days.length - 1]
	// the days and the handler are new on every render, so the listener reads
	// the latest ones from a ref instead of being re-attached each time
	const latest = useRef({ first, onChange })
	useEffect(() => {
		latest.current = { first, onChange }
	})
	useEffect(() => {
		function handleKeyDown(e: KeyboardEvent) {
			if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return
			if (e.defaultPrevented) return
			const target = e.target as HTMLElement
			if (
				target.isContentEditable ||
				["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)
			) {
				return
			}
			e.preventDefault()
			const { first, onChange } = latest.current
			if (e.key === "ArrowLeft") {
				onChange(subtractUnit(first, 7, "day"))
			} else {
				onChange(addUnit(first, 7, "day"))
			}
		}
		window.addEventListener("keydown", handleKeyDown)
		return () => window.removeEventListener("keydown", handleKeyDown)
	}, [])
	return (
		<Inline gap="4" alignItems="center">
			<DateTimeInput
				rightAlign
				value={first}
				renderMenu={({ toggle }) => (
					<Button level="tertiary" status="primary" size="sm" onClick={toggle}>
						<Icons.CalendarDays /> Jump to date
					</Button>
				)}
				onChange={(date) => date && onChange(startOf(date, "day"))}
			/>
			<Inline gap="2" alignItems="center">
				<Button
					size="sm"
					title="Previous week"
					onClick={() => onChange(subtractUnit(first, 7, "day"))}
				>
					<Icons.ChevronDown rotate="90" />
				</Button>
				<Text fontWeight="semibold">
					{formatDate(first, "D MMM, YYYY")} → {formatDate(last, "D MMM, YYYY")}
				</Text>
				<Button
					size="sm"
					title="Next week"
					onClick={() => onChange(addUnit(first, 7, "day"))}
				>
					<Icons.ChevronDown rotate="270" />
				</Button>
			</Inline>
		</Inline>
	)
}

export function CalendarTable({
	roomTypes,
	days,
	today,
	otaMealPlan,
}: {
	roomTypes: THotelCalendarAriRoomType[]
	days: Date[]
	today: Date
	otaMealPlan: THotelOtaMealPlan
}) {
	return (
		<Box overflowX="auto" borderWidth="1" borderColor="emphasis" rounded="md">
			<Box
				as="table"
				width="full"
				style={{ borderCollapse: "separate", borderSpacing: 0 }}
			>
				<thead>
					<tr>
						<Box
							as="th"
							position="sticky"
							bgColor="subtle"
							color="muted"
							textAlign="left"
							fontWeight="semibold"
							paddingY="2"
							paddingX="4"
							{...getCellBorders({ isLastColumn: false, isLastRow: false })}
							borderRightWidth="1"
							borderColor="default"
							style={{ left: 0, zIndex: 1, minWidth: "12rem" }}
						>
							Room Type <Icons.ArrowLeft rotate="270" />
						</Box>
						{days.map((day, index) => {
							const dayOfWeek = getUnit(day, "day")
							const isWeekend = dayOfWeek === 0 || dayOfWeek === 6
							const isToday = isSame(today, day, "date")
							return (
								<Box
									as="th"
									key={dateToQuery(day)}
									bgColor={isWeekend ? undefined : "default"}
									color={isWeekend ? "on_emphasis" : "muted"}
									textAlign="center"
									fontWeight="normal"
									paddingY="2"
									paddingX="4"
									{...getCellBorders({
										isLastColumn: index === days.length - 1,
										isLastRow: false,
									})}
									style={{
										minWidth: "8rem",
										backgroundColor: isWeekend
											? WEEKEND_HEADER_BACKGROUND
											: undefined,
										boxShadow: isToday
											? `inset 0 3px 0 0 ${themeContractVars.border.primary}`
											: undefined,
									}}
								>
									<Stack alignItems="center">
										<Box
											fontSize="sm"
											style={{
												color: isToday
													? themeContractVars.fg.primary
													: undefined,
											}}
											fontWeight={isToday ? "semibold" : "normal"}
										>
											{formatDate(day, "ddd")}
										</Box>
										<Box
											fontWeight="semibold"
											rounded="md"
											paddingX="1"
											color={isToday || isWeekend ? "on_emphasis" : "default"}
											style={{
												backgroundColor: isToday
													? themeContractVars.bg.primary_emphasis
													: undefined,
											}}
										>
											{formatDate(day, "D MMM")}
										</Box>
									</Stack>
								</Box>
							)
						})}
					</tr>
				</thead>
				<tbody>
					{/* room types come in the hotel's room order */}
					{roomTypes.map(
						({ ota_room_type: roomType, dates: ariDays }, rowIndex) => {
							const isLastRow = rowIndex === roomTypes.length - 1
							return (
								<tr key={roomType.id}>
									<Box
										as="td"
										position="sticky"
										bgColor="default"
										padding="4"
										verticalAlign="middle"
										{...getCellBorders({ isLastColumn: false, isLastRow })}
										borderRightWidth="1"
										borderColor="default"
										style={{ left: 0, zIndex: 1, height: "84px" }}
									>
										<Text fontWeight="semibold">{roomType.name}</Text>
										<Text fontSize="sm" color="muted">
											{pluralize("Room", roomType.no_of_rooms, true)}
										</Text>
									</Box>
									{/* the api lines each room type's dates up with the range */}
									{days.map((day, columnIndex) => (
										<CalendarCell
											key={dateToQuery(day)}
											ariDay={ariDays[columnIndex]}
											day={day}
											today={today}
											roomType={roomType}
											otaMealPlan={otaMealPlan}
											isLastColumn={columnIndex === days.length - 1}
											isLastRow={isLastRow}
										/>
									))}
								</tr>
							)
						}
					)}
				</tbody>
			</Box>
		</Box>
	)
}

// The last column and row leave their edge to the border around the grid
function getCellBorders({
	isLastColumn,
	isLastRow,
}: {
	isLastColumn: boolean
	isLastRow: boolean
}) {
	return {
		borderColor: "muted",
		borderRightWidth: isLastColumn ? "0" : "1",
		borderBottomWidth: isLastRow ? "0" : "1",
	} as const
}

function CalendarCell({
	ariDay,
	day,
	today,
	roomType,
	otaMealPlan,
	isLastColumn,
	isLastRow,
}: {
	ariDay: THotelCalendarAriDay
	day: Date
	today: Date
	roomType: Pick<THotelOtaRoomType, "name">
	otaMealPlan: THotelOtaMealPlan
	isLastColumn: boolean
	isLastRow: boolean
}) {
	const status = getCellStatus(ariDay)
	const price = getCellPrice(ariDay)
	const isPast = day < today
	const tone = isPast ? PAST_CELL_TONE : getCellTone(status, ariDay.available)
	const label = isPast
		? "Past date"
		: status === "open"
			? ariDay.available === null
				? "–"
				: `${ariDay.available} Left`
			: status === "sold_out"
				? "Sold out"
				: "Stop Sell"
	const showBookedCount = isPast || status === "open"
	return (
		<Box
			as="td"
			padding="2"
			verticalAlign="top"
			{...getCellBorders({ isLastColumn, isLastRow })}
			style={{
				backgroundImage: tone.hatch,
				backgroundColor: tone.bgColor,
			}}
		>
			<Tooltip
				content={
					<CalendarCellTooltip
						ariDay={ariDay}
						status={status}
						price={price}
						day={day}
						roomType={roomType}
						otaMealPlan={otaMealPlan}
					/>
				}
			>
				<Stack gap="2" alignItems="center">
					<Inline
						justifyContent="between"
						alignItems="center"
						gap="1"
						width="full"
					>
						<Box
							as="span"
							paddingX="2"
							paddingY="1"
							rounded="md"
							fontSize="xs"
							fontWeight="semibold"
							style={{
								backgroundColor: tone.chipBackground,
								color: tone.labelColor,
							}}
							whiteSpace="nowrap"
						>
							{label}
						</Box>
						{showBookedCount && ariDay.bookings_count !== 0 ? (
							<Inline
								gap="1"
								alignItems="center"
								paddingX="1"
								rounded="md"
								fontSize="xs"
								fontWeight="semibold"
								color={isPast ? "muted" : "accent"}
								bgColor={isPast ? undefined : "accent"}
								style={isPast ? { backgroundColor: "#E0E1E6" } : undefined}
							>
								<Icons.Bed /> {ariDay.bookings_count}
							</Inline>
						) : null}
					</Inline>

					<Box
						textAlign="center"
						fontSize="md"
						fontWeight="semibold"
						style={{
							textDecoration:
								!isPast && status === "sold_out" && price
									? "line-through"
									: undefined,
							color: tone.priceColor,
						}}
					>
						{!isPast && status === "stop_sell" ? (
							"–"
						) : price ? (
							<Money money={price} />
						) : (
							"–"
						)}
					</Box>
				</Stack>
			</Tooltip>
		</Box>
	)
}

function CalendarCellTooltip({
	ariDay,
	status,
	price,
	day,
	roomType,
	otaMealPlan,
}: {
	ariDay: THotelCalendarAriDay
	status: TCalendarCellStatus
	price: TMoney | null
	day: Date
	roomType: Pick<THotelOtaRoomType, "name">
	otaMealPlan: THotelOtaMealPlan
}) {
	const showAvailability = status !== "open" || ariDay.available !== null
	return (
		<Stack gap="1">
			<Text fontSize="xs" color="subtle">
				{formatDate(day, "ddd, D MMM")}
			</Text>
			<Text fontSize="sm" fontWeight="semibold">
				{roomType.name} | {otaMealPlan.name}
			</Text>
			{status !== "stop_sell" && price ? (
				<Money money={price} fontWeight="semibold" />
			) : null}
			{showAvailability ? <Divider marginY="1" /> : null}
			{status === "open" ? (
				ariDay.available !== null ? (
					<Text
						style={{
							color: "#27D366",
						}}
						fontWeight="semibold"
						fontSize="xs"
					>
						{pluralize("Room", ariDay.available, true)} available
					</Text>
				) : null
			) : (
				<Text color="danger" fontWeight="semibold">
					{status === "sold_out" ? "Sold out" : "Stop Sell"}
				</Text>
			)}
		</Stack>
	)
}

// The grey between the plain header and the black of `emphasis`, so weekends
// stand out without shouting. Blended from theme vars to follow the dark theme.
const WEEKEND_HEADER_BACKGROUND = `color-mix(in srgb, ${themeContractVars.fg.muted} 70%, ${themeContractVars.bg.default})`

function hatch(color: string, stripe: string) {
	return `repeating-linear-gradient(45deg, ${color}, ${color} 6px, ${stripe} 6px, ${stripe} 12px)`
}

// Past dates always render muted, regardless of what the cell's status was
const PAST_CELL_TONE = {
	bgColor: "#F0F0F3",
	hatch: undefined,
	chipBackground: "#E0E1E6",
	labelColor: "#60646C",
	priceColor: "#60646C",
}

// Open days are tinted by how many rooms are left. Sold out and stopped-sell
// days are hatched so an empty-looking price still reads as closed at a glance.
function getCellTone(status: TCalendarCellStatus, available: number | null) {
	if (status === "sold_out") {
		return {
			bgColor: "#F9F9FB",
			hatch: hatch("#F9F9FB", "#F0F0F3"),
			chipBackground: "#F0F0F3",
			labelColor: "#60646C",
			priceColor: "#F9F9FB",
		}
	}
	if (status === "stop_sell") {
		return {
			bgColor: "#FEEBE780",
			hatch: hatch("#FEEBE780", "#FDBDAF1A"),
			chipBackground: "#FEEBE7",
			labelColor: "#D13415",
			priceColor: "#EC8E7B",
		}
	}
	// nothing known about the rooms, so neither plenty nor running low
	if (available === null) {
		return {
			bgColor: "#F0F0F3",
			hatch: undefined,
			chipBackground: "#E0E1E6",
			labelColor: "#60646C",
			priceColor: "#60646C",
		}
	}
	const isLow = available <= LOW_ROOMS_LEFT
	return {
		bgColor: isLow ? "#FFF4C0" : "#ECFDF5",
		hatch: undefined,
		chipBackground: isLow ? "#FFE574" : "#C6FCE4",
		labelColor: isLow ? "#CA4300" : "#007C53",
		priceColor: isLow ? "#CA4300" : "#007C53",
	}
}

// A stop sell is pushed on purpose, so it wins over a day that is also sold out
function getCellStatus(ariDay: THotelCalendarAriDay): TCalendarCellStatus {
	if (ariDay.is_stop_sell) return "stop_sell"
	if (ariDay.is_sold_out) return "sold_out"
	return "open"
}

// The api sends the rate as a decimal string, so parse it until it sends money
function getCellPrice(ariDay: THotelCalendarAriDay): TMoney | null {
	if (ariDay.rate_source === null) return null
	if (ariDay.base_rate_before_tax === null) return null
	return moneyParseByDecimal(
		Number(ariDay.base_rate_before_tax),
		ariDay.currency
	)
}
